import type { gameInfoType, LanguageType } from '@/pages/opinion/types/common';
import { useMemo } from 'react';
import { feedsColumnsFunc } from '../columns/feeds';
import { useIntl } from 'umi';
import type { SortBy } from 'tea-component/lib/table/addons';
import { getColumnsAndSortColumns } from '../utils';
import { useMetricsChannel } from '@op/hooks/useMetricsChannel';
import { useGetTopicFilter } from '@op/hooks/useTranslateTopic';

export function useGameStoreColumns({
  gameInfo,
  languages,
  renderKey = '',
  sort,
  setSort,
  isExcludeInvalid,
  setIsExcludeInvalid,
  showSentiment = false,
  total,
  logCommentMap,
  logLoading,
  setLanguages,
  channelList = [],
  wholeGameInfo,
}: {
  gameInfo: gameInfoType;
  languages: Array<LanguageType>;
  renderKey?: string | { origin: string[]; zh: string[]; en: string[] };
  sort: SortBy<any>;
  setSort: (sort: SortBy<any>) => void;
  isExcludeInvalid?: boolean;
  setIsExcludeInvalid?: (value: boolean) => void;
  showSentiment?: boolean;
  total?: number;
  logCommentMap?: { [key: string]: string };
  logLoading?: boolean;
  setLanguages: (value: LanguageType[]) => void;
  channelList?: string[];
  wholeGameInfo?: any;
}) {
  const { formatMessage } = useIntl();
  const MergeChannelIconElement = useMetricsChannel({ gameInfo });
  const { topicList } = useGetTopicFilter({ gameInfo });

  const channelListMemo = useMemo(
    () => channelList,
    [JSON.stringify(channelList)],
  );

  const getKeys = (): [Array<string>, string] => {
    const languageSort = ['origin', 'en', 'zh'];
    const languageList = languageSort.filter((a: any) =>
      languages?.includes(a),
    );
    const { entity_type = 'mobile' } = gameInfo;

    const keysMap: any = {
      mobile: [
        'channelName',
        'rating',
        ...languageList,
        'validation',
        'commentTime',
        'market',
        'version',
        'topic',
      ],
      pc: [
        'channelName',
        'rating',
        ...languageList,
        'validation',
        'monitorSource',
        'commentTime',
        'language',
        'topic',
        'refunded',
        'steamId',
        'starred',
        'receivedForFreeTag',
        'steamPurchase',
        'earlyAccessTag',
        'funny',
        'helpful',
      ],
      console: [
        'channelName',
        'rating',
        ...languageList,
        'validation',
        'monitorSource',
        'commentTime',
        'language',
        'topic',
        'feedsPlatform',
      ],
    };

    if (channelListMemo?.find(item => item === 'meta_store')) {
      keysMap.pc.push('meta_like', 'meta_dislike');
    }

    return [keysMap[entity_type], languageList[0]];
  };

  const { columns, selectSortKeys, sortColumns } = useMemo(() => {
    const [keys, firstLanguage] = getKeys();
    const { entity_type } = gameInfo;
    const columnsMap = feedsColumnsFunc({
      formatMessage,
      entityType: entity_type,
      firstLanguage,
      renderKey,
      topicList,
      sort,
      setSort,
      excludeInvalidInfo: [isExcludeInvalid, setIsExcludeInvalid],
      showSentiment,
      total,
      logCommentMap,
      logLoading,
      MergeChannelIconElement,
      languages,
      setLanguages,
      wholeGameInfo,
    });

    return getColumnsAndSortColumns(keys, columnsMap);
  }, [
    formatMessage,
    languages,
    renderKey,
    topicList,
    sort,
    isExcludeInvalid,
    showSentiment,
    total,
    logCommentMap,
    logLoading,
  ]);

  return { columns, selectSortKeys, sortColumns };
}
